import { IconButton, Tooltip, Box } from '@material-ui/core';
import { MatUiCheckbox } from 'components/main';
import React from 'react';
import styled from 'styled-components/macro';
import { ReactComponent as ThreePointIcon } from 'assets/images/blackThreePoint.svg';
import AddIcon from '@material-ui/icons/Add';
import { AssigneeAvatar, InboxAvatar } from '..';
import GmailLabels from 'components/workdesk/ticket-group-table/GmailLabels';
import { Statuses } from 'store/models/Statuses';
import { ReactComponent as PreviewEmailIcon } from 'assets/images/preview-mail.svg';
import { ReactComponent as AssignTicketLoading } from 'assets/images/assign-loading.svg';

export interface EachWorkdeskColumn {
  dataKey: string;
  label: null | string;
  width: string;
  textAlign?: string;
  renderCell?: (valueAndOthers: { [key: string]: any }) => any;
  sortable?: boolean;
  sortByField?: string;
}
const workdeskColumns: EachWorkdeskColumn[] = [
  {
    label: null,
    width: '2vw',
    dataKey: 'id',
    textAlign: 'right',
    renderCell: ({
      value: id,
      ticket,
      handleToggleSelectTicket,
      isSelected,
      isShowMoreInfo,
      canSelectTicket,
    }) => {
      return (
        <SCheckboxWrapper
          className={isShowMoreInfo ? 'on-expand' : ''}
        >
          <MatUiCheckbox
            value={isSelected}
            disabled={!canSelectTicket}
            withoutMarginRight
            handleChange={(checked) =>
              handleToggleSelectTicket(checked, id)
            }
          />
        </SCheckboxWrapper>
      );
    },
  },
  {
    label: null,
    width: '2.5vw',
    dataKey: 'rowNo',
  },
  {
    dataKey: 'subject',
    label: null,
    width: '33vw',
    textAlign: 'left',
    renderCell: ({
      value: subject,
      ticket,
      isShowMoreInfo,
      canOpenTicket,
      ...otherProps
    }) => {
      const { handleDirect } = otherProps;
      return (
        <SGmailLabelsAndSubject>
          <GmailLabels
            labels={ticket.labels}
            isShowMoreInfo={isShowMoreInfo}
          />
          <SSubjectText>
            <Tooltip
              title={
                <STooltipText>
                  {Statuses.isReadyToViewStatus(ticket.status) && (
                    <STooltipTextBold>
                      Click to go to ticket:
                    </STooltipTextBold>
                  )}
                  {`${subject}`}
                </STooltipText>
              }
            >
              <STextDirect
                onClick={() => handleDirect(ticket)}
                className={!canOpenTicket ? 'unclickable' : ''}
              >{`${
                subject !== null ? `${subject}` : '-'
              }`}</STextDirect>
            </Tooltip>
          </SSubjectText>
        </SGmailLabelsAndSubject>
      );
    },
  },
  {
    dataKey: 'id',
    label: '',
    width: '2.8vw',
    renderCell: ({
      value,
      handlePreviewEmailBody,
      isShowMoreInfo,
    }) => {
      return (
        <Tooltip title="Preview Email">
          <SIconButton
            onClick={(e) => handlePreviewEmailBody(e, value)}
          >
            <SPreviewEmailIcon
              className={isShowMoreInfo ? 'on-expand' : ''}
            />
          </SIconButton>
        </Tooltip>
      );
    },
  },
  {
    dataKey: 'assignee',
    label: 'Assignee',
    sortable: true,
    sortByField: 'assigneeId',
    renderCell: ({
      value: assignee,
      handleAssign,
      ticket,
      assignLoadingTicketIdMap,
      canAssignOrUnassignTicket,
    }) => {
      const { id: ticketId } = ticket;
      if (!canAssignOrUnassignTicket) {
        return (
          <Tooltip title={(assignee && `${assignee.name}`) || ''}>
            <SAssign>
              <SBox>
                <AssigneeAvatar assignee={assignee} />
              </SBox>
            </SAssign>
          </Tooltip>
        );
      } else {
        const isLoading =
          assignLoadingTicketIdMap &&
          assignLoadingTicketIdMap[ticketId];
        return (
          <Tooltip
            title={assignee ? `${assignee.name}` : 'Assign To Me'}
          >
            <SAssign>
              {!assignee && (
                <SOpacityHover
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAssign(ticketId);
                  }}
                >
                  <AddIcon />
                </SOpacityHover>
              )}
              {isLoading && (
                <SOpacity>
                  <AssignTicketLoading />
                </SOpacity>
              )}
              <SBox className={isLoading ? 'loading' : ''}>
                <AssigneeAvatar assignee={assignee} />
              </SBox>
            </SAssign>
          </Tooltip>
        );
      }
    },
    width: '5vw',
  },
  // {
  //   dataKey: 'id',
  //   label: 'Ticket ID',
  //   width: '10vw',
  // },
  {
    dataKey: 'inbox',
    label: 'Inbox',
    width: '3vw',
    renderCell: ({ value: inbox }) => (
      <Box justifyContent="center" display="flex">
        <InboxAvatar inbox={inbox} />
      </Box>
    ),
  },
  {
    dataKey: 'bookingNos',
    label: 'Booking No.',
    width: '10vw',
    renderCell: ({ value: bookingNos, ticket, isShowMoreInfo }) => (
      <SBookingNo>
        {/* right now only show the first booking no */}
        {bookingNos && !!bookingNos.length && bookingNos[0]}
        {/* only show the length if has more than one booking nos */}
        {bookingNos && bookingNos.length > 1 && (
          <SCountBooking
            className={`${isShowMoreInfo ? 'on-expand' : ''}`}
          >{`${bookingNos.length}`}</SCountBooking>
        )}
      </SBookingNo>
    ),
  },
  {
    dataKey: 'receivedTimeText', // received at?
    label: 'Received Time',
    width: '6.5vw',
    sortable: true,
    sortByField: 'receivedTime',
    renderCell: ({ value, ticket }) => {
      return ticket && ticket.receivedTimeHoverText ? (
        <Tooltip title={ticket && ticket.receivedTimeHoverText}>
          <SReceiveTimeText>{value}</SReceiveTimeText>
        </Tooltip>
      ) : (
        <SReceiveTimeText>{value}</SReceiveTimeText>
      );
    },
  },
  {
    dataKey: 'updateDateText', // received at?
    label: 'Update Date',
    width: '6.5vw',
    sortable: true,
    sortByField: 'updateDate',
    renderCell: ({ value, ticket }) => {
      return ticket && ticket.updateDateHoverText ? (
        <Tooltip title={ticket && ticket.updateDateHoverText}>
          <SReceiveTimeText>{value}</SReceiveTimeText>
        </Tooltip>
      ) : (
        <SReceiveTimeText>{value}</SReceiveTimeText>
      );
    },
  },
  {
    dataKey: 'si',
    label: 'SI',
    width: '2.5vw',
    renderCell: ({
      value: si,
      handleSINonSI,
      ticket,
      manualSiLoadingIdMap,
      isShowMoreInfo,
    }) =>
      manualSiLoadingIdMap && manualSiLoadingIdMap[ticket.id] ? (
        <SSiLoading>
          <AssignTicketLoading />
        </SSiLoading>
      ) : (
        // <SSIIcon
        //   src={si ? SIIcon : nonSIIcon}
        //   onClick={(e) => {
        //     e.stopPropagation();
        //     handleSINonSI(!si, ticket.id);
        //   }}
        // />
        <SCheckboxWrapper
          className={isShowMoreInfo ? 'on-expand' : ''}
        >
          <MatUiCheckbox
            disabled={!Statuses.canMarkSiNonSi(ticket.status)}
            withoutMarginRight
            value={si}
            handleChange={(checked) =>
              handleSINonSI(checked, ticket.id)
            }
          />
        </SCheckboxWrapper>
      ),
  },
  {
    dataKey: 'id',
    label: '',
    width: '2.5vw',
    renderCell: ({
      value: id,
      dropdownId,
      handleThreePoint,
      renderDropdown,
      ticket,
      numIndex,
      numTicket,
      isShowMoreInfo,
      canOpenTicket,
      canSelectTicket,
    }) => (
      <SThreePointWrapper
        className={`${
          canOpenTicket || canSelectTicket ? 'clickable' : 'disabled'
        } ${isShowMoreInfo ? 'on-expand' : ''} `}
        onClick={(e) => {
          e.stopPropagation();
          handleThreePoint(dropdownId !== id ? id : '');
        }}
      >
        <ThreePointIcon />
        {dropdownId === ticket.id &&
          renderDropdown(ticket, numTicket, numIndex)}
      </SThreePointWrapper>
    ),
  },
];

export default workdeskColumns;
const SCheckboxWrapper = styled.div.attrs((props) => ({
  className: props.className,
}))`
  z-index: 999999;
  &.on-expand {
    & .MuiCheckbox-colorPrimary.Mui-checked {
      color: white;
    }
  }
`;

const SReceiveTimeText = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.bold};
`;
const SIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 5px;
  }
`;
const SGmailLabelsAndSubject = styled.div`
  margin-right: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const SSubjectText = styled.div`
  display: inline;
`;

const STextDirect = styled.div`
  display: inline;
  width: fit-content;
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  &:hover {
    text-decoration-line: underline;
  }
  &.unclickable {
    &:hover {
      text-decoration-line: none;
    }
  }
`;

const SThreePointWrapper = styled.div.attrs((props) => ({
  className: props.className,
}))`
  padding: 2px 1px;
  &.on-expand {
    & > svg > path {
      fill: white;
    }
  }
  &.disabled {
    & > svg > path {
      fill: ${(props) => props.theme.palette.text.disabled};
    }
  }
`;

const STooltipText = styled.div`
  font-size: 13px;
`;
const STooltipTextBold = styled.div`
  margin-right: 5px;
  font-weight: bolder;
  font-size: 15px;
  line-height: 1.4;
`;

const SOpacityHover = styled.div`
  background: white;
  opacity: 0.8;
  position: absolute;
  z-index: 1;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  opacity: 0;
  &:hover {
    opacity: 0.5;
  }
  & > svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    fill: black;
  }
`;

const SBox = styled.div`
  margin: 3px 0;
  &.loading {
    opacity: 0.5;
  }
`;

const SOpacity = styled.div`
  position: absolute;
  z-index: 1;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -3px;
  width: 36px;
  height: 36px;
  & > svg {
    width: 25px;
    height: 25px;
  }
`;

const SAssign = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SBookingNo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SCountBooking = styled.div`
  display: flex;
  background: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.common.white};
  border-radius: 20px;
  width: 20px;
  height: 20px;
  margin-left: 5px;
  justify-content: center;
  align-items: center;
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  font-size: 12px;
  &.on-expand {
    color: ${(props) => props.theme.palette.primary.main};
    background: ${(props) => props.theme.palette.common.white};
  }
`;
const SPreviewEmailIcon = styled(PreviewEmailIcon)`
  // filter: grayscale(1);
  border-radius: 4px;
  width: 25px;
  height: 25px;
  &.on-expand {
    stroke: ${(props) => props.theme.palette.primary.main};
    stroke-width: 5px;
    & path {
      fill: white;
    }
  }
`;
const SSiLoading = styled.div`
  & > svg {
    width: 25px;
    height: 25px;
  }
`;
